import { useCallback, useEffect, useRef } from 'react'

const useVisibleElement = ({
  rootMargin,
  onVisible,
  threshold,
}: {
  rootMargin: string
  onVisible: () => void
  threshold?: number
}) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const hasTriggeredRef = useRef(false)

  const handleIntersection = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      if (!hasTriggeredRef.current && entry.isIntersecting) {
        hasTriggeredRef.current = true
        onVisible()
      }
    },
    [onVisible]
  )

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin,
      threshold: threshold || 0,
    })

    observerRef.current.observe(element)

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [rootMargin, threshold, handleIntersection])

  return {
    elementRef,
  }
}

export default useVisibleElement
